import React, { createContext, useContext, useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { useLocation } from 'wouter';
import toast from 'react-hot-toast';
import { supabaseOrganizerService } from '../supabase/organizer.service';
import { OrganizerProfile } from '../types/organizer.types';
import { Event } from '../types/event.types';
import { useAuth } from './AuthContext';
import { useGoogleMaps } from './GoogleMapsContext';
import { smartListenerService } from '../supabase/SmartListenerService';
import { OrganizerAnalyticsService, DashboardData, AnalyticsPeriod } from '../services/OrganizerAnalyticsService';
import { AnalyticsService } from '../services/AnalyticsService';

/**
 * Turn a raw error (often a technical/English network message like
 * "Failed to fetch") into a readable, user-facing message.
 * PRD-robustesse-organisateur-reseau-faible.md (item 6)
 */
function toReadableProfileErrorMessage(err: unknown): string {
  const raw = err instanceof Error ? err.message : typeof err === 'string' ? err : '';

  if (/failed to fetch|networkerror|load failed|err_internet_disconnected|err_network|err_connection/i.test(raw)) {
    return 'Connection issue — could not load your profile. Check your network.';
  }
  if (/abort|timeout|timed out/i.test(raw)) {
    return 'Loading your profile is taking too long. Check your connection.';
  }

  return raw || 'Could not load your profile. Please try again.';
}

// 🔧 Interface pour les counts d'événements
interface EventCounts {
  active: number;
  draft: number;
  past: number;
}

// KPIs simplifiés pour la page profil (all time)
interface AllTimeKpis {
  totalViews: number;
  totalClicks: number;
  totalLikes: number;
}

interface OrganizerContextType {
  organizerProfile: OrganizerProfile | null;
  publicProfiles: Record<string, any>;
  loading: boolean;
  // 🔧 PRD-robustesse-organisateur-reseau-faible.md (item 4): erreur de
  // chargement du profil (distincte de "pas de profil") + moyen de réessayer.
  profileError: string | null;
  retryLoadProfile: () => void;
  updateProfile: (data: Partial<OrganizerProfile>) => Promise<void>;
  getPublicProfile: (organizerId: string) => Promise<any>;
  isGoogleMapsLoaded: boolean;
  // 🚀 NOUVEAU: Events organisateur centralisés (channel TOUJOURS actif)
  organizerEvents: Event[];
  eventCounts: EventCounts;
  eventsLoading: boolean;
  // 🚀 NOUVEAU: Analytics temps réel
  dashboardData: DashboardData | null;
  analyticsLoading: boolean;
  refreshAnalytics: () => Promise<void>;
  // 🆕 Sélecteur de période (7 jours / 1 mois)
  analyticsPeriod: AnalyticsPeriod;
  setAnalyticsPeriod: (period: AnalyticsPeriod) => void;
  // 📊 Stats all time pour la page profil
  alltimeKpis: AllTimeKpis | null;
}

export const OrganizerContext = createContext<OrganizerContextType | undefined>(undefined);

export const OrganizerProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const auth = useAuth();
  const { isLoaded: isGoogleMapsLoaded } = useGoogleMaps();
  const [location] = useLocation();
  const [organizerProfile, setOrganizerProfile] = useState<OrganizerProfile | null>(null);
  const [publicProfiles, setPublicProfiles] = useState<Record<string, any>>({});
  const [loading, setLoading] = useState(false);
  // 🔧 PRD-robustesse-organisateur-reseau-faible.md (item 4)
  const [profileError, setProfileError] = useState<string | null>(null);
  const [profileRetryToken, setProfileRetryToken] = useState(0);

  // 🔧 Détecter si on est sur une route organizer (pour éviter les appels analytics inutiles)
  const isOrganizerRoute = useMemo(() => location.startsWith('/organizer'), [location]);

  // 🚀 NOUVEAU: État pour les events organisateur (centralisé)
  const [organizerEvents, setOrganizerEvents] = useState<Event[]>([]);
  const [eventCounts, setEventCounts] = useState<EventCounts>({ active: 0, draft: 0, past: 0 });
  const [eventsLoading, setEventsLoading] = useState(false);

  // 🚀 NOUVEAU: État pour les analytics
  const [dashboardData, setDashboardData] = useState<DashboardData | null>(null);
  const [analyticsLoading, setAnalyticsLoading] = useState(false);
  // 🆕 État pour la période d'analyse
  const [analyticsPeriod, setAnalyticsPeriod] = useState<AnalyticsPeriod>('7days');
  // 📊 État pour les stats all time (page profil)
  const [alltimeKpis, setAlltimeKpis] = useState<AllTimeKpis | null>(null);

  // 🔧 ConsumerId stable pour éviter les boucles infinies
  const eventsConsumerIdRef = useRef<string>(`context-events-${Math.random().toString(36).substr(2, 9)}`);

  // 🔧 FIX: Stabiliser la liste des eventIds pour éviter les re-fetch inutiles
  const stableEventIds = useMemo(() => {
    const ids = organizerEvents.map(e => e.id).sort();
    return JSON.stringify(ids);
  }, [organizerEvents]);

  useEffect(() => {
    // console.log( - SILENCIEUX EN PROD"🌍 OrganizerContext - Google Maps chargé:", isGoogleMapsLoaded);
  }, [isGoogleMapsLoaded]);

  useEffect(() => {
    if (auth.loading) return;
    
    if (!auth.user) {
      // console.log( - SILENCIEUX EN PROD"Pas d'utilisateur connecté");
      return;
    }

    setLoading(true);
    setProfileError(null);

    const unsubscribe = supabaseOrganizerService.subscribeToProfile(
      auth.user.id,
      (profile: OrganizerProfile | null) => {
        setOrganizerProfile(profile);
        setProfileError(null);
        setLoading(false);
      },
      (error: Error) => {
        console.error('Erreur chargement profil organisateur:', error);
        // 🔧 FIX (item 4 - PRD robustesse réseau faible): avant ce fix, une
        // erreur réseau transitoire laissait `organizerProfile` à `null` pour
        // toujours (dashboard vide) sans jamais l'expliquer ni permettre de
        // réessayer. On expose maintenant l'erreur (et un retry) sans écraser
        // un profil déjà chargé.
        setProfileError(toReadableProfileErrorMessage(error));
        setLoading(false);
      }
    );

    return () => unsubscribe();
  // 🔧 FIX: Utiliser auth.user?.id au lieu de auth.user pour éviter les re-triggers
  // quand Supabase envoie plusieurs événements auth avec le même utilisateur
  // profileRetryToken permet de forcer un nouveau chargement (bouton retry).
  }, [auth.user?.id, auth.loading, profileRetryToken]);

  // 🔧 FIX (item 4/2 - PRD robustesse réseau faible): rendre l'échec de
  // chargement du profil visible (toast + action "Retry"), sinon il restait
  // silencieux (juste un console.error) et le dashboard semblait vide sans raison.
  useEffect(() => {
    if (!profileError) return;

    toast(
      (t) => (
        <span>
          {profileError}
          <button
            onClick={() => {
              toast.dismiss(t.id);
              setProfileRetryToken((prev) => prev + 1);
            }}
            style={{
              marginLeft: 8,
              background: 'none',
              border: 'none',
              padding: 0,
              color: 'inherit',
              textDecoration: 'underline',
              cursor: 'pointer',
              font: 'inherit',
            }}
          >
            Retry
          </button>
        </span>
      ),
      { duration: 6000, icon: '⚠️' }
    );
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [profileError]);

  // 📊 Tracker la connexion organisateur (métrique "Connexions Orgas" du dashboard admin)
  // Émis 1 seule fois par session navigateur (sessionStorage), quand un organisateur
  // authentifié (profil chargé) est sur une route /organizer/* (= utilise son dashboard).
  useEffect(() => {
    if (!isOrganizerRoute || !organizerProfile?.id) return;

    try {
      if (sessionStorage.getItem('w2d_org_login_tracked')) return;
      sessionStorage.setItem('w2d_org_login_tracked', '1');
      AnalyticsService.trackOrganizerLogin(organizerProfile.id);
    } catch {
      // sessionStorage indisponible (navigation privée stricte) : on ne track pas
      // plutôt que de risquer un event par navigation.
    }
  }, [organizerProfile?.id, isOrganizerRoute]);

  // 🚀 NOUVEAU: Abonnement aux events organisateur (TOUJOURS actif tant que l'user est connecté)
  useEffect(() => {
    if (auth.loading || !auth.user) {
      setOrganizerEvents([]);
      setEventCounts({ active: 0, draft: 0, past: 0 });
      return;
    }

    console.log(`🏢 [OrganizerContext] Démarrage listener events pour ${auth.user.id}`);
    setEventsLoading(true);

    const unsubscribe = smartListenerService.subscribeToOrganizerEvents(
      auth.user.id,
      eventsConsumerIdRef.current,
      (eventsFromListener: Event[]) => {
        console.log(`📡 [OrganizerContext] Reçu ${eventsFromListener.length} events`);
        setOrganizerEvents(eventsFromListener);
        setEventsLoading(false);

        // Calculer les counts groupés par série
        const now = new Date();
        const singleEvents = eventsFromListener.filter(e => !e.seriesId);
        const seriesEvents = eventsFromListener.filter(e => e.seriesId);

        const seriesMap = new Map<string, Event[]>();
        seriesEvents.forEach(e => {
          if (e.seriesId) {
            if (!seriesMap.has(e.seriesId)) seriesMap.set(e.seriesId, []);
            seriesMap.get(e.seriesId)!.push(e);
          }
        });

        const singleCounts = {
          active: singleEvents.filter(e => e.status === 'active' && (!e.datetime || e.datetime >= now)).length,
          draft: singleEvents.filter(e => e.status === 'draft').length,
          past: singleEvents.filter(e => (e.status === 'active' || e.status === 'cancelled') && e.datetime && e.datetime < now).length,
        };

        let seriesActive = 0, seriesDraft = 0, seriesPast = 0;
        seriesMap.forEach((occurrences) => {
          const firstOccurrence = occurrences[0];
          if (firstOccurrence.status === 'draft') {
            seriesDraft++;
          } else {
            const hasActiveOccurrences = occurrences.some(e => e.status === 'active' && e.datetime && e.datetime >= now);
            const hasPastOccurrences = occurrences.some(e => (e.status === 'active' || e.status === 'cancelled') && e.datetime && e.datetime < now);
            if (hasActiveOccurrences) seriesActive++;
            if (hasPastOccurrences) seriesPast++;
          }
        });

        setEventCounts({
          active: singleCounts.active + seriesActive,
          draft: singleCounts.draft + seriesDraft,
          past: singleCounts.past + seriesPast,
        });
      }
    );

    return () => {
      console.log(`🧹 [OrganizerContext] Cleanup listener events`);
      unsubscribe();
    };
  // 🔧 FIX: Utiliser auth.user?.id pour stabiliser les dépendances
  }, [auth.user?.id, auth.loading]);

  // 🚀 NOUVEAU: Charger les analytics dashboard
  // 🔧 FIX: Utiliser stableEventIds au lieu de organizerEvents pour éviter les re-fetch
  const refreshAnalytics = useCallback(async () => {
    if (!auth.user) return;

    setAnalyticsLoading(true);
    try {
      const activeEventsCount = eventCounts.active;
      // Parse les IDs depuis la string stable
      const eventIds: string[] = JSON.parse(stableEventIds);
      const data = await OrganizerAnalyticsService.getDashboardData(
        auth.user.id,
        activeEventsCount,
        analyticsPeriod,
        eventIds
      );
      setDashboardData(data);
    } catch (error) {
      console.error('❌ [OrganizerContext] Erreur chargement analytics:', error);
    } finally {
      setAnalyticsLoading(false);
    }
  }, [auth.user, eventCounts.active, analyticsPeriod, stableEventIds]);

  // 🆕 Recharger les analytics quand la période change
  // 🔧 FIX: Seulement sur les routes /organizer/* pour éviter les appels inutiles
  useEffect(() => {
    if (auth.user && !auth.loading && isOrganizerRoute) {
      refreshAnalytics();
    }
  }, [analyticsPeriod, isOrganizerRoute]); // eslint-disable-line react-hooks/exhaustive-deps

  // 📊 Charger les stats all time une fois (pour la page profil)
  // 🔧 FIX: Utiliser stableEventIds au lieu de organizerEvents pour éviter les re-fetch
  // 🔧 FIX: Seulement sur les routes /organizer/* pour éviter les appels inutiles
  useEffect(() => {
    // Ne pas charger les stats all time si on n'est pas sur une route organizer
    if (!isOrganizerRoute) return;

    const fetchAlltimeKpis = async () => {
      if (!auth.user || auth.loading) {
        setAlltimeKpis(null);
        return;
      }

      try {
        // Parse les IDs depuis la string stable
        const eventIds: string[] = JSON.parse(stableEventIds);
        const data = await OrganizerAnalyticsService.getDashboardData(
          auth.user.id,
          eventCounts.active,
          'alltime',
          eventIds
        );
        setAlltimeKpis({
          totalViews: data.kpis.apparitionsEvents,
          totalClicks: data.kpis.clicsEvents,
          totalLikes: data.kpis.likesEvents
        });
      } catch (error) {
        console.error('❌ [OrganizerContext] Erreur chargement alltime KPIs:', error);
      }
    };

    // Fetch alltime KPIs when events are loaded (stableEventIds !== '[]')
    const hasEvents = stableEventIds !== '[]';
    if (hasEvents || eventCounts.active === 0) {
      fetchAlltimeKpis();
    }
  // 🔧 FIX: Utiliser auth.user?.id pour stabiliser les dépendances
  }, [auth.user?.id, auth.loading, stableEventIds, eventCounts.active, isOrganizerRoute]);

  // 🚀 NOUVEAU: Abonnement aux analytics temps réel
  // 🔧 FIX: Seulement sur les routes /organizer/* pour éviter les appels inutiles
  useEffect(() => {
    // Ne pas charger les analytics si on n'est pas sur une route organizer
    if (!isOrganizerRoute) {
      return;
    }

    if (auth.loading || !auth.user) {
      setDashboardData(null);
      return;
    }

    console.log(`📊 [OrganizerContext] Chargement analytics (route: ${location})`);

    // Charger les analytics initiales
    refreshAnalytics();

    // S'abonner aux mises à jour temps réel
    const unsubscribe = OrganizerAnalyticsService.subscribeToRealtimeUpdates(
      auth.user.id,
      (update) => {
        // Mettre à jour les KPIs incrémentalement
        setDashboardData(prev => {
          if (!prev) return prev;
          return {
            ...prev,
            kpis: {
              ...prev.kpis,
              apparitionsEvents: prev.kpis.apparitionsEvents + (update.apparitionsEvents || 0),
              clicsEvents: prev.kpis.clicsEvents + (update.clicsEvents || 0),
              likesEvents: prev.kpis.likesEvents + (update.likesEvents || 0),
            }
          };
        });
      }
    );

    return () => {
      console.log(`🧹 [OrganizerContext] Cleanup listener analytics`);
      unsubscribe();
    };
  // 🔧 FIX: Utiliser auth.user?.id pour stabiliser les dépendances
  // Note: refreshAnalytics est intentionnellement omis pour éviter les boucles
  }, [auth.user?.id, auth.loading, isOrganizerRoute, location]);

  // 🔧 PRD-robustesse-organisateur-reseau-faible.md (item 4): permet de
  // réessayer manuellement le chargement du profil après une erreur réseau.
  const retryLoadProfile = useCallback(() => {
    setProfileError(null);
    setProfileRetryToken((prev) => prev + 1);
  }, []);

  // Méthode publique pour récupérer un profil organisateur (utilisée par les visiteurs)
  const getPublicProfile = useCallback(async (organizerId: string): Promise<OrganizerProfile | null> => {
    return await supabaseOrganizerService.getOrganizerProfile(organizerId);
  }, []);

  const updateProfile = async (data: Partial<OrganizerProfile>) => {
    if (!auth.user) return;

    try {
      await supabaseOrganizerService.saveProfile(auth.user.id, {
        ...organizerProfile,
        ...data
      } as OrganizerProfile);
      setOrganizerProfile(prev => prev ? { ...prev, ...data } : null);
    } catch (error) {
      console.error('Erreur lors de la mise à jour du profil:', error);
      throw error;
    }
  };

  return (
    <OrganizerContext.Provider value={{
      organizerProfile,
      publicProfiles,
      loading,
      profileError,
      retryLoadProfile,
      updateProfile,
      getPublicProfile,
      isGoogleMapsLoaded,
      // 🚀 NOUVEAU: Events centralisés
      organizerEvents,
      eventCounts,
      eventsLoading,
      // 🚀 NOUVEAU: Analytics centralisées
      dashboardData,
      analyticsLoading,
      refreshAnalytics,
      // 🆕 Sélecteur de période
      analyticsPeriod,
      setAnalyticsPeriod,
      // 📊 Stats all time pour la page profil
      alltimeKpis,
    }}>
      {children}
    </OrganizerContext.Provider>
  );
};

export const useOrganizer = () => {
  const context = useContext(OrganizerContext);
  if (context === undefined) {
    throw new Error('useOrganizer must be used within an OrganizerProvider');
  }
  return context;
};